import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useEffect, useRef, useState } from 'react';
import { WizardData } from '@/store/webinar/wizardStore';
import { useToast } from '@/hooks/use-toast';

interface AutosaveOptions {
  debounceMs?: number;
  onSave?: (data: WizardData) => Promise<void>;
}

export function useWebinarAutosave(data: WizardData, options: AutosaveOptions = {}) {
  useLanguage();
  const { debounceMs = 2000, onSave } = options;
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const prevDataRef = useRef<string>(JSON.stringify(data));

  useEffect(() => {
    const currentData = JSON.stringify(data);
    
    // Only autosave if data actually changed
    if (currentData === prevDataRef.current) {
      return;
    }

    prevDataRef.current = currentData;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(async () => {
      setIsSaving(true);
      try {
        if (onSave) {
          await onSave(data);
        } else {
          // Default save to Supabase
          // TODO: Implement actual Supabase call
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        setLastSaved(new Date());
        toast({
          title: translateInterfaceText("Rascunho salvo"),
          duration: 2000,
        });
      } catch (error) {
        console.error('Erro ao salvar rascunho:', error);
        toast({
          title: translateInterfaceText("Erro ao salvar"),
          description: translateInterfaceText("Não foi possível salvar o rascunho. Tente novamente."),
          variant: 'destructive',
        });
      } finally {
        setIsSaving(false);
      }
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, debounceMs, onSave, toast]);

  return {
    isSaving,
    lastSaved,
  };
}
